// One labelled trace ID with its copy-link and copy-ID buttons. The modal shows
// this three times — for the execution, its parent, and the cache source.

import React from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { Button, Flex, P, Popover } from '@decisiv/ui-components';
import Copy from '@decisiv/iconix/lib/components/Copy';
import Link from '@decisiv/iconix/lib/components/Link';

import { InlineIconButton } from './style';

const inspectorUrlFor = (traceId) =>
  `${window.location.origin}/toolkit/log_inspector/${traceId}`;

const CopyButton = ({ icon, label, onCopy }) => (
  <InlineIconButton>
    <Button
      icon={icon}
      aria-label={label}
      variant="ghost"
      onClick={onCopy}
      size="small"
    />
  </InlineIconButton>
);

CopyButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired,
};

// Only the primary trace ID confirms the copy; the parent and cache-source rows
// shipped without it, so the feedback is opt-in rather than applied to all three.
const WithFeedback = ({ message, children }) => (
  <Popover
    placement="bottom-start"
    manageEvents="click"
    zIndex={999}
    target={({ ref }) => <span ref={ref}>{children}</span>}
  >
    <Flex padding={1}>
      <span>{message}</span>
    </Flex>
  </Popover>
);

WithFeedback.propTypes = {
  message: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function TraceIdField({
  label,
  traceId = '',
  confirmCopy = false,
}) {
  const linkButton = (
    <CopyButton
      icon={Link}
      label={t`Copy link to log inspector`}
      onCopy={() => navigator.clipboard.writeText(inspectorUrlFor(traceId))}
    />
  );
  const idButton = (
    <CopyButton
      icon={Copy}
      label={t`Copy trace ID`}
      onCopy={() => navigator.clipboard.writeText(traceId)}
    />
  );

  return (
    <>
      <Flex flexDirection="row">
        <P color="charcoal" weight="semibold">
          {label}&nbsp;
        </P>
      </Flex>
      <Flex flexDirection="row">
        <P size="small" weight="regular" color="alaskanHusky">
          {traceId}
        </P>
        {confirmCopy ? (
          <>
            <WithFeedback message={t`Copied Link to Log Inspector`}>
              {linkButton}
            </WithFeedback>
            <WithFeedback message={t`Copied ID`}>{idButton}</WithFeedback>
          </>
        ) : (
          <>
            {linkButton}
            {idButton}
          </>
        )}
      </Flex>
    </>
  );
}

TraceIdField.propTypes = {
  label: PropTypes.node.isRequired,
  traceId: PropTypes.string,
  confirmCopy: PropTypes.bool,
};
